import { FC, useEffect, useState } from 'react';
import { PlusCircleSvg } from '../svgs';
import AddStrawLevelDataModal from '../AddStrawLevelData/AddStrawLevelDataModal';
import { LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../constants/common';
import { AddStrawLevelDataModalMode, AddStrawModalState } from '../../types/interfaces/addStrawLevelData.interfaces';
import { EmbryoStraw } from '../../types/interfaces';
import { specimenNeedsStrawDetails } from '../../utils/commonUtils';

export interface SelectedEmbryoStraw {
  embryoStrawId: number;
  quantity: number;
  selected: boolean;
}

interface SelectEmbryoStrawsProps {
  embryoStraws: EmbryoStraw[];
  specimenId?: number;
  quantityAvailable?: number;
  specimenTypeId?: number;
  allowToAddStrawDetails?: boolean;
  onStrawDataAdded?: () => void;
  onChange: (selected: SelectedEmbryoStraw[], totalQuantity: number) => void;
}

const SelectEmbryoStraws: FC<SelectEmbryoStrawsProps> = ({
  embryoStraws,
  specimenId,
  quantityAvailable,
  specimenTypeId,
  allowToAddStrawDetails = false,
  onStrawDataAdded,
  onChange,
}) => {
  const [selectedRows, setSelectedRows] = useState<SelectedEmbryoStraw[]>([]);
  const [addStrawModalState, setAddStrawModalState] = useState<AddStrawModalState | null>(null);

  const showAddStrawButton =
    allowToAddStrawDetails &&
    specimenNeedsStrawDetails({ specimenTypeId, quantityAvailable, embryoStraws }) &&
    specimenId !== undefined;

  useEffect(() => {
    const initRows = (embryoStraws ?? []).map((straw: EmbryoStraw) => ({
      embryoStrawId: straw.embryoStrawId,
      quantity: 0,
      selected: false,
    }));

    setSelectedRows(initRows);
    onChange([], 0);
  }, [embryoStraws]);

  const handleAddStrawOpen = () => {
    if (specimenId === undefined) return;
    setAddStrawModalState({ specimenId });
  };

  const handleCheckboxChange = (index: number, checked: boolean) => {
    const updatedSelectedRows = [...selectedRows];
    const selectedStraw = embryoStraws[index];

    updatedSelectedRows[index].selected = checked;
    updatedSelectedRows[index].quantity = checked ? selectedStraw.embryoCountPerStraw ?? 0 : 0;

    setSelectedRows(updatedSelectedRows);

    const selectedEmbryoStraws = updatedSelectedRows.filter(row => row.selected);
    const totalSelectedQuantity = selectedEmbryoStraws.reduce((acc, row) => acc + row.quantity, 0);

    onChange(selectedEmbryoStraws, totalSelectedQuantity);
  };

  const totalQuantity = selectedRows.reduce(
    (totalQuantitySum, selectedRow) => (selectedRow.selected ? totalQuantitySum + selectedRow.quantity : totalQuantitySum),
    0,
  );

  return (
    <>
      <div className="specimen-table embryo-straws-table">
        <table>
          <thead>
            <tr>
              <th>{TABLE_HEADER_CONSTANTS.SELECT}</th>
              <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>
              <th>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}</th>
              <th>{TABLE_HEADER_CONSTANTS.EMBRYO_GRADE}</th>
              <th>{TABLE_HEADER_CONSTANTS.EMBRYO_STAGE}</th>
              <th>{TABLE_HEADER_CONSTANTS.QUANTITY}</th>
            </tr>
          </thead>
          <tbody>
            {embryoStraws?.length ? (
              embryoStraws.map((straw: EmbryoStraw, index: number) => (
                <tr key={straw.embryoStrawId}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedRows[index]?.selected || false}
                      onChange={e => handleCheckboxChange(index, e.target.checked)}
                    />
                  </td>
                  <td>{straw.embryoStrawCode}</td>
                  <td>{straw.embryoStrawNumber ?? '-'}</td>
                  <td>{straw.embryoGrade || '-'}</td>
                  <td>{straw.embryoStage ?? '-'}</td>
                  <td>{straw.embryoCountPerStraw ?? '-'}</td>
                </tr>
              ))
            ) : showAddStrawButton ? (
              <tr>
                <td>
                  <input type="checkbox" disabled />
                </td>
                <td>
                  <button className="button icon add-straw-details" type="button" onClick={handleAddStrawOpen}>
                    <PlusCircleSvg />
                  </button>
                </td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>{quantityAvailable ?? 0}</td>
              </tr>
            ) : (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center' }}>
                  {LABEL_CONSTANTS.NO_RESULTS_FOUND}
                </td>
              </tr>
            )}
            <tr>
              <td colSpan={5}>{LABEL_CONSTANTS.TOTAL_QUANTITY}:</td>
              <td>{totalQuantity}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {addStrawModalState && (
        <AddStrawLevelDataModal
          isOpen
          onClose={() => setAddStrawModalState(null)}
          onSuccess={onStrawDataAdded}
          mode={AddStrawLevelDataModalMode.Specimen}
          specimenIds={[addStrawModalState.specimenId]}
          preselectedSpecimenId={addStrawModalState.specimenId}
        />
      )}
    </>
  );
};

export default SelectEmbryoStraws;
